import { SolutionPage } from "@/components/shared/SolutionPage";
import { LeadPipelineMockup } from "@/components/shared/LeadPipelineMockup";
import { MessageSquare, Target, Megaphone, Repeat, Zap, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { SystemLadder } from "@/components/shared/SystemLadder";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";
import { NeedACarHighlight } from "@/components/case-studies/NeedACarHighlight";

const LeadGen = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Lead Generation"
      seo={{
        title: "Lead Generation System for Small Business | Frayze",
        description: "Get a steady stream of new leads every month. We build inbound, outbound, and reactivation systems that fill your pipeline and feed your CRM automatically.",
        keywords: "lead generation system, small business marketing, inbound marketing, outbound lead generation, lead reactivation, sales pipeline growth",
        canonical: "https://frayze.ca/solutions/lead-generation-system/"
      }}
      hero={{
        badge: "Frayze Growth System: Step 3",
        badgeColor: "text-indigo-600",
        title: "A Steady Stream of Leads. Every Single Month.",
        description: "Tired of relying on word-of-mouth and hoping the phone rings? We build you a system that brings in new leads, follows up automatically, and feeds your calendar, so growth stops being a gamble.",
        buttonColor: "bg-indigo-600 hover:bg-indigo-700",
        gradientParams: "from-indigo-500/5 via-background to-background",
        primaryCta: { text: "Fill My Calendar", link: "/book-consultation/?tier=growth" },
        secondaryCta: { text: "View Pricing", link: "/pricing/" },
        visual: <LeadPipelineMockup />
      }}
      offRamp={{
        message: "Most businesses start one step earlier: get the website and CRM foundation working first, then come back here to fill the pipeline.",
        ctaText: "Start with Step 1",
        ctaLink: "/website-launch-program/",
        accentColor: "text-indigo-600"
      }}
      problem={{
        title: "The Real Problem: Feast or Famine Growth",
        description: (
          <>
            Most businesses market in bursts. They rely on referrals, boost posts when cash is tight, and pause when leads slow down.
            <br /><br />
            <strong>This creates a revenue rollercoaster. Growth feels unpredictable because it is.</strong>
          </>
        ),
        gridCols: 2,
        cards: [
          { value: "Feast or Famine", label: "Spikes, droughts, and anxiety. Marketing fails because there's nowhere for demand to go.", highlight: false },
          { value: "The Growth Engine", label: "Steady, predictable leads month after month. No more spikes, no more droughts, just a full pipeline you can count on.", highlight: true, valueColor: "text-indigo-600" }
        ]
      }}

      systemModel={{
        title: "How the Growth Engine Works",
        description: "Three steady streams of new leads working for you at once.",
        variant: "cards",
        items: [
          {
            title: "They Come to You",
            description: "Search-friendly pages, helpful content, and smart forms turn interest into leads and qualify every inquiry automatically.",
            icon: MessageSquare,
            color: "bg-indigo-500"
          },
          {
            title: "You Go to Them",
            description: "Instead of waiting for the phone to ring, targeted outreach finds your next customer for you.",
            icon: Target,
            color: "bg-indigo-600"
          },
          {
            title: "Win Back Old Leads",
            description: "Past customers and cold leads are automatically re-engaged, unlocking revenue already sitting in your database.",
            icon: Repeat,
            color: "bg-indigo-700"
          }
        ]
      }}

      layers={{
        title: "What's Included in the Growth Engine",
        description: "Built on the CRM Growth Foundation.",
        theme: "indigo",
        items: [
          {
            title: "Inbound Systems",
            icon: MessageSquare,
            items: ["Conversion-optimized pages", "Funnel tracking & attribution", "Call and form routing"]
          },
          {
            title: "Outbound Systems",
            icon: Target,
            items: ["Cold & warm outreach", "AI qualification logic", "Automated booking handoff"]
          },
          {
            title: "Lifecycle Systems",
            icon: Repeat,
            items: ["Long-term nurture", "Reactivation workflows", "Lead scoring"]
          }
        ]
      }}

      interdependence={{
        title: "Marketing Fails Without a System Underneath It",
        description: (
          <>
            Most marketing doesn’t fail because of traffic. It fails because there’s nowhere for demand to go. <br />
            The Marketing System feeds qualified opportunities into the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM Foundation</Link>, then tracks what actually converts.
            <br /><br />
            It runs on capabilities like <Link to="/platform/speed-to-lead/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">Speed-to-Lead</Link> and <Link to="/platform/database-reactivation/" className="font-bold underline decoration-emerald-500/50 hover:text-emerald-600 transition-colors">Database Reactivation</Link>: answer every new lead in seconds, and mine the ones already sitting in your database.
          </>
        ),
        icon: Zap,
        quote: "More ads don’t fix this. A system does."
      }}
      useCases={{
        title: "Automotive-Led. Cross-Industry Proven.",
        items: [
          { title: "Automotive", description: "Inbound inventory leads + Outbound buyer follow-up.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Quote requests to booked estimates + Seasonal reactivation.", link: "/industries/trades/" },
          { title: "Professional & B2B", description: "Long-cycle nurture + Outbound prospecting.", link: "/solutions/" }
        ]
      }}
      socialProof={{
        title: "Proof in Practice: Results Compound",
        quote: "\"We stopped guessing where our customers came from. The Marketing System clarified exactly which channels were profitable.\"",
        icon: TrendingUp,
        ctaLink: "/case-studies/global-aluminum-lead-generation/"
      }}
      cta={{
        title: "Stop Guessing Where Your Next Customer Comes From",
        description: "Let's map out a system that keeps your pipeline full. Book a free strategy session and see what it could look like for your business.",
        primaryCta: { text: "Book My Free Strategy Session", link: "/book-consultation/" },
        secondaryCta: { text: "View Pricing", link: "/pricing/" },
        color: "bg-indigo-600 hover:bg-indigo-700"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          {/* Campaign vs System Comparison */}
          <section className="container mx-auto px-6 max-w-4xl pt-12">
            <div className="reveal bg-foreground text-background rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 blur-3xl rounded-full pointer-events-none" />

              <div className="text-center mb-12 relative z-10">
                <h2 className="text-3xl font-bold mb-4">Campaign Thinking vs. System Thinking</h2>
                <p className="text-white/70">The Growth Engine is built for the second.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-12 relative z-10">
                <div className="reveal reveal-delay-1 border-r border-white/10 md:pr-12">
                  <h3 className="text-xl font-bold mb-6 text-indigo-300">Campaign Thinking</h3>
                  <ul className="space-y-4">
                    {["Short-term", "Reactive", "Channel-by-channel", "Hard to measure"].map(item => (
                      <li key={item} className="flex items-center gap-3 text-white/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="reveal reveal-delay-2 md:pl-4">
                  <h3 className="text-xl font-bold mb-6 text-indigo-300">System Thinking</h3>
                  <ul className="space-y-4">
                    {["Always-on", "Predictable", "Unified", "Compounding over time"].map(item => (
                      <li key={item} className="flex items-center gap-3 text-white font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Paid Acceleration */}
          <section className="bg-indigo-50 dark:bg-indigo-950/20 py-24">
            <div className="container mx-auto px-6 text-center max-w-4xl">
              <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-6">
                <Megaphone className="w-4 h-4" /> Paid Acceleration
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl font-bold mb-6">Ads Work Best When the System Is Ready</h2>
              <p className="reveal reveal-delay-2 text-lg text-muted-foreground mb-8 leading-relaxed">
                Once the Growth Engine is converting profitably, <Link to="/solutions/ad-management-scale/" className="text-indigo-600 hover:text-indigo-700 font-semibold underline">paid media</Link> becomes an accelerator, not a gamble.
                We manage Google & Meta ads, retargeting, and full attribution tied to booked revenue. See how we did this for{" "}
                <Link to="/case-studies/global-aluminum-lead-generation/" className="text-indigo-600 hover:text-indigo-700 font-semibold underline">
                  Global Aluminum Solutions
                </Link>
                , where a full audit caught 49% of measured ad spend landing on a dead page, and the rebuilt campaigns cut cost per lead from $96 to $59 in their first month.
              </p>
              <div className="reveal reveal-delay-3 text-xl font-medium text-foreground">
                Ads fuel the system. They <span className="text-indigo-600 relative inline-block">are not<span className="absolute bottom-0 left-0 w-full h-1 bg-indigo-200 -z-10"></span></span> the system.
              </div>
            </div>
          </section>

          {/* Flagship Case Study: Need A Car */}
          <section className="container mx-auto px-6 max-w-4xl">
            <div className="reveal">
              <NeedACarHighlight focus="leads" theme="blue" />
            </div>
          </section>

          {/* System Ladder Position */}
          <section className="container mx-auto px-6 pb-12">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl font-bold mb-4">Where This Fits in the Growth Ladder</h2>
            </div>
            <div className="reveal reveal-delay-1 bg-slate-50 dark:bg-slate-900/50 border rounded-2xl p-8 overflow-hidden max-w-5xl mx-auto">
              <SystemLadder activeTier="growth" />
            </div>
          </section>

          <ServiceFAQ
            title="Questions About The Growth Engine"
            description="Understanding how we generate predictable demand."
            items={[
              {
                question: "Do you just sell me a list of leads?",
                answer: "No. Selling lists violates privacy laws (CASL/CAN-SPAM) and is ineffective. We build YOU a system that generates exclusive, real-time leads that belong only to you."
              },
              {
                question: "What industries does this work for?",
                answer: "It works best for high-ticket service businesses (Contractors, Auto, MedSpa, Professional Services) where a single lead is worth $500+. It is less effective for low-margin e-commerce items under $50."
              },
              {
                question: "Is this compliant with Spam Laws (CASL & CAN-SPAM)?",
                answer: "Yes. Our system includes built-in unsubscribe management, A2P 10DLC registration for SMS compliance, and follows all CASL (Canada) and CAN-SPAM (US) requirements for consent and identification."
              },
              {
                question: "How fast will I get leads?",
                answer: "Inbound (SEO) takes time to build momentum. Outbound and Database Reactivation typically generate appointments within the first 7-14 days of launch."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default LeadGen;
